/**
 * /account/pets/:id/assessment — book an evaluation day (migration 0099).
 *
 * The wizard's "needs an evaluation first" blocker and the pet page both
 * deep-link here. Simple date + time (evaluations are staff-supervised and
 * the tenant confirms); POST /api/customer/assessments creates the booking
 * and the page hands off to the booking detail.
 */
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CustomerApiError, customerApi } from '../customer_api';
import { formatMoney } from '../lib/format';

export interface CustomerAssessmentSettings {
  daycareRequiresAssessment: boolean;
  boardingRequiresAssessment: boolean;
  ownerMustStay: boolean;
  validityMonths: number | null;
  noteHtml: string | null;
  service: {
    id: string;
    name: string;
    description: string | null;
    defaultDurationMin: number | null;
    defaultPriceMinor: number;
    currency: string;
  } | null;
}

export interface CustomerPetAssessment {
  petId: string;
  petName: string;
  status: string;
  passedAt: string | null;
  expiresAt: string | null;
  isCurrent: boolean;
  openBooking: { bookingId: string; scheduledAt: string | null } | null;
  requiredFor: { daycare: boolean; boarding: boolean };
  canBook: boolean;
  bookAssessmentUrl: string;
}

/** Tenant-authored HTML — same trust boundary as the waiver page. */
export function TenantHtml({ html }: { html: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.innerHTML = html;
  }, [html]);
  return <div ref={ref} data-testid="assessment-note" />;
}

function tomorrowIso(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export function AssessmentBook() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<CustomerAssessmentSettings | null>(null);
  const [pet, setPet] = useState<CustomerPetAssessment | null>(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('10:00');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      customerApi<CustomerAssessmentSettings>('/api/customer/assessments/settings'),
      customerApi<CustomerPetAssessment>(`/api/customer/assessments/pets/${id}`),
    ])
      .then(([s, p]) => {
        setSettings(s);
        setPet(p);
      })
      .catch(() => setError('Could not load evaluation details. Please try again.'));
  }, [id]);

  async function submit() {
    if (!id || !date || !time) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await customerApi<{ bookingId: string }>('/api/customer/assessments', {
        method: 'POST',
        body: JSON.stringify({ petId: id, date, startTime: time }),
      });
      navigate(`/account/bookings/${r.bookingId}`);
    } catch (err) {
      if (err instanceof CustomerApiError) {
        const b = err.body as { error?: string; message?: string } | null;
        if (b?.error === 'assessment_already_scheduled') {
          setError('This pet already has an evaluation day booked.');
        } else if (b?.error === 'assessment_not_configured') {
          setError('Evaluation days cannot be booked online yet — please contact us.');
        } else {
          setError(b?.message ?? 'Could not book the evaluation. Please try again.');
        }
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-10">
      <Link
        to={id ? `/account/pets/${id}` : '/account/pets'}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}
      >
        <ArrowLeft size={14} strokeWidth={1.75} />
        Back to pet
      </Link>
      <h1 style={{ fontSize: '1.6rem', fontWeight: 500, margin: '0.75rem 0 0.25rem' }}>
        Book an evaluation day{pet ? ` for ${pet.petName}` : ''}
      </h1>
      {error && (
        <p role="alert" style={{ color: 'var(--destructive, #b3261e)', fontSize: '0.9rem' }}>
          {error}
        </p>
      )}
      {!settings || !pet ? (
        <p style={{ fontSize: '0.9rem', color: 'var(--muted-foreground)' }}>
          {error ? '' : 'Loading…'}
        </p>
      ) : !settings.service ? (
        <p style={{ fontSize: '0.9rem' }}>
          Evaluation days cannot be booked online yet — please contact us to arrange one.
        </p>
      ) : pet.openBooking ? (
        <p style={{ fontSize: '0.9rem' }}>
          {pet.petName} already has an evaluation day booked.{' '}
          <Link to={`/account/bookings/${pet.openBooking.bookingId}`}>View the booking</Link>.
        </p>
      ) : (
        <>
          <p style={{ fontSize: '0.9rem', color: 'var(--muted-foreground)', margin: '0 0 1rem' }}>
            {settings.service.name}
            {settings.service.defaultDurationMin != null && (
              <>
                {' · about '}
                <span className="num">{settings.service.defaultDurationMin}</span> minutes
              </>
            )}
            {' · '}
            {settings.service.defaultPriceMinor > 0 ? (
              <span className="num">
                {formatMoney(settings.service.defaultPriceMinor, settings.service.currency)}
              </span>
            ) : (
              'Free'
            )}
          </p>
          {settings.ownerMustStay && (
            <p style={{ fontSize: '0.9rem', margin: '0 0 1rem' }} data-testid="owner-must-stay">
              Please plan to stay with {pet.petName} for the whole evaluation.
            </p>
          )}
          {settings.noteHtml && (
            <div style={{ fontSize: '0.9rem', margin: '0 0 1.25rem' }}>
              <TenantHtml html={settings.noteHtml} />
            </div>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            style={{ display: 'grid', gap: '0.75rem' }}
          >
            <label style={{ display: 'grid', gap: 4, fontSize: '0.85rem' }}>
              Date
              <input
                type="date"
                value={date}
                min={tomorrowIso()}
                required
                onChange={(e) => setDate(e.currentTarget.value)}
                style={{ padding: '0.5rem', borderRadius: 8, border: '1px solid var(--border)' }}
              />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: '0.85rem' }}>
              Arrival time
              <input
                type="time"
                value={time}
                required
                onChange={(e) => setTime(e.currentTarget.value)}
                style={{ padding: '0.5rem', borderRadius: 8, border: '1px solid var(--border)' }}
              />
            </label>
            <button
              type="submit"
              disabled={submitting || !date || !time}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '0.6rem 1rem',
                borderRadius: 8,
                border: 'none',
                background: 'var(--primary, #004AAD)',
                color: 'white',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              Book evaluation day
            </button>
          </form>
        </>
      )}
    </div>
  );
}
